import type { NsDict } from '../ru';

export const verify: NsDict<'verify'> = {
  title: 'Game verification',
  chooseFile: 'Choose package file…',
  hintBefore: 'Verification runs in your browser using the same open-source code as the command-line tool ',
  hintAfter: '. The server takes no part in it apart from providing its public key.',
  notJson: 'The file is not a JSON verification package',
  httpError: 'error {status}',
  metaGame: 'Game',
  metaComposition: 'Composition',
  metaP0: 'P₀ bar (UTC)',
  metaSource: 'Source',
  sourceSynthetic: 'synthetic data — not real quotes',
  sourceAlpaca: 'Alpaca Market Data API v2, minute bars, {feed} feed',
  busy: 'Recalculating…',
  exportHint: 'Exports for comparison with external quote sources (UTC, separator “;”):',
  exportQuotes: 'Stock quotes, CSV',
  exportIndex: 'Index bars, CSV',
  exportActions: 'Action log, CSV',
  exportPackage: 'Package, JSON',
  // ── заголовки проверок ──
  'check.title.format': 'File format',
  'check.title.signature': 'Server signature (Ed25519)',
  'check.title.commitment': 'Commitment: composition fixed before betting',
  'check.title.aggregate': 'Index bars recalculated from stock quotes',
  'check.title.prices': 'P₀, P₁ and change Δ',
  'check.title.timeline': 'Timeline: actions before lock, reveals by stage',
  'check.title.contributions': 'Player contributions match the ledger',
  'check.title.settlement': 'Payouts recalculated from scratch',
  'check.title.ledger': 'Credit ledger balances',
  // ── итог ──
  'check.summary.unrecognized': 'file not recognised',
  'check.summary.ok': 'All checks passed ({ok} of {total}).',
  'check.summary.okWarn': 'All checks passed ({ok} of {total}, with remarks).',
  'check.summary.fail': 'Failed: {titles}.',
  'check.format.notPackage': 'this is not a Market Table verification package',
  // ── подпись ──
  'check.signature.missing': 'signature missing or unknown algorithm',
  'check.signature.keyMismatch': 'the key in the file does not match the server’s published key',
  'check.signature.keyTrusted': 'key {keyId} matches the one published by the server',
  'check.signature.keyUntrusted': 'key {keyId} (compare with /api/verification-key)',
  'check.signature.intact': 'package contents unchanged since issue',
  'check.signature.invalid': 'signature does not match the contents — the file has been modified',
  'check.signature.error': 'signature check error: {error}',
  // ── обязательство ──
  'check.commitment.hashOk': 'SHA-256(salt:parameters) = {hash}… matches the published hash',
  'check.commitment.hashMismatch': 'hash mismatch: computed {computed}, published {published}',
  'check.commitment.badJson': 'commitment parameters are not valid JSON',
  'check.commitment.symbolsMismatch': 'composition in commitment {committed} ≠ revealed {revealed}',
  'check.commitment.symbols': 'composition in commitment: {symbols}',
  'check.commitment.basePricesMismatch': 'base prices in commitment differ from the revealed ones',
  'check.commitment.basePricesOk': 'base prices match',
  'check.commitment.p0Mismatch': 'P₀ bar time in commitment does not match',
  'check.commitment.formulaMismatch': 'formula/base/tick in commitment differ from those applied',
  'check.commitment.sessionMismatch': 'session ID in commitment does not match',
  // ── бары индекса ──
  'check.aggregate.barMismatch': 'bar k={k} ({time}): {fields}',
  'check.aggregate.field': '{field}: shown {shown}, recalculated {recomputed}',
  'check.aggregate.unknownFormula': 'formula {formula} is unknown to this verifier version or does not fit {n} {n:component|components}',
  'check.aggregate.ok': '{n} {n:bar matches|bars match} the recalculation by formula {formula}',
  'check.aggregate.source': 'quote source: {source}',
  // ── цены ──
  'check.prices.p0Mismatch': 'P₀ must be {base}: recalculated {recomputed}, in settlement {settled}',
  'check.prices.p0': 'P₀ = {p0|num} (close of bar {time})',
  'check.prices.noTarget': 'no target candle to recalculate P₁',
  'check.prices.p1Mismatch': 'P₁: in settlement {settled}, recalculated {recomputed}',
  'check.prices.deltaMismatch': 'Δ in ticks: in settlement {settled}, recalculated {recomputed}',
  'check.prices.p1': 'P₁ = {p1|num} (close of target minute {time}), Δ = {sign}{delta|num}',
  'check.prices.notNeeded': 'outcome “{kind}” — P₁ not required for settlement',
  // ── хронология ──
  'check.timeline.late': 'actions after lock: {actions}',
  'check.timeline.allBefore': 'all actions ({n}) made before forecasts were locked at {time}',
  'check.timeline.compositionEarly': 'composition revealed before round 3 began',
  'check.timeline.compositionOk': 'composition revealed at {time} — at the start of round 3',
  'check.timeline.viewerLate': 'changes to your forecast after lock: {n}',
  'check.timeline.viewerOk': 'your forecast changed {n} {n:time|times}, all before lock',
  'check.timeline.finalMismatch': 'the final forecast in the settlement differs from your last change',
  'check.timeline.unordered': 'action log is out of order',
  // ── вклады ──
  'check.contributions.mismatch': '{name}: ledger shows ante {ante|cr}, rounds {rounds|cr}; settlement shows ante {settledAnte|cr}, rounds {settledRounds|cr}',
  'check.contributions.ok': 'for {n} {n:participant|participants}, antes and round bets match the ledger',
  // ── расчёт ──
  'check.settlement.error': 'recalculation failed: {error}',
  'check.settlement.statusMismatch': 'status: “{settled}” in package, recalculated “{recomputed}”',
  'check.settlement.noPlayer': 'player {playerId} missing from settlement',
  'check.settlement.playerMismatch': '{name}: payout {payout|cr} / refund {refund|cr} in package, recalculated {rePayout|cr} / {reRefund|cr}',
  'check.settlement.commissionMismatch': 'commission by round: {settled} in package, recalculated {recomputed}',
  'check.settlement.bankMismatch': 'withheld by the house: {settled|cr} in package, recalculated {recomputed|cr}',
  'check.settlement.unbalanced': 'the recalculated settlement does not balance',
  'check.settlement.outcome': 'outcome: {kind}',
  'check.settlement.outcomeReason': 'outcome: {kind} ({reason})',
  'check.settlement.kind.market': 'by market',
  'check.settlement.kind.walkover': 'one participant left',
  'check.settlement.kind.cancel': 'cancellation',
  'check.settlement.totals': 'pots: {pots}; commission by round {commission|cr}; withheld {withheld|cr}',
  'check.settlement.payout': '{name}: payout {payout|cr}',
  'check.settlement.payoutError': '{name}: payout {payout|cr}, forecast error {error|num} pts',
  'check.settlement.wrongDirection': 'payout to a player with the wrong direction',
  // ── проводки ──
  'check.ledger.escrowNonZero': 'game account balance: {net|cr} (must be 0)',
  'check.ledger.escrowZero': 'game account closed at zero',
  'check.ledger.playerMismatch': '{name}: ledger payouts {payout|cr} / refunds {refund|cr}, settlement {settledPayout|cr} / {settledRefund|cr}',
  'check.ledger.commissionMismatch': 'commission in ledger {ledger|cr}, in settlement {settled|cr}',
  'check.ledger.ok': 'debited {debited|cr} = paid {paid|cr} + refunded {refunded|cr} + house {bank|cr}',
};
